<template>
  <component :is="isSMViewport ? 'div' : BasicScrollingWithGradient">
    <div class="result-list">
      <basic-message
        v-if="$props.results.length === 0"
        class="result-list__message"
        :severity="NOTIFICATION.WARNING"
        :closeable="false"
        :content="$t(`careProvider.result.empty.text.${$props.avmNetwork}`)"
        :title-label="$t('careProvider.result.empty.title')"
      />
      <template v-else-if="hasOnlyAlternativeResults">
        <basic-message
          class="result-list__message"
          :severity="NOTIFICATION.WARNING"
          :closeable="false"
          :content="$t(`careProvider.result.alternative.${$props.avmNetwork}.text`)"
          :title-label="$t(`careProvider.result.alternative.${props.avmNetwork}.title`)"
        />
        <div
          class="caption-regular result-list__alternative-results"
          v-text="$t('careProvider.result.alternative.caption')"
        />
      </template>
      <result-card
        v-for="careProvider in displayResults"
        :key="careProvider.zsrNumber"
        :auto-scroll="$props.autoScroll"
        :avm-network="$props.avmNetwork"
        :care-provider="careProvider"
        :contract-start-date="$props.contractStartDate"
        :hovered="$props.hovered"
        :is-alternative-avm-network="hasOnlyAlternativeResults"
        :selected="$props.selected"
        :show-details="$props.showDetails"
        @add="(zsrNumber) => $emit('add', zsrNumber)"
        @mouseover="(zsrNumber) => $emit('mouseover', zsrNumber)"
        @mouseout="(zsrNumber) => $emit('mouseout', zsrNumber)"
        @select="(zsrNumber) => $emit('select', zsrNumber)"
      />
    </div>
  </component>
</template>

<script setup>
  import { computed, watchEffect } from 'vue'

  import useBrowser from '@shared/hooks/useBrowser'

  import { BasicMessage, BasicScrollingWithGradient } from '@shared/components/Basic'
  import ResultCard from '@shared/components/CareProvider/ResultCard'

  import { NOTIFICATION } from '@shared/config/constants'

  // HOOKS
  const { isSMViewport } = useBrowser()

  // INIT
  defineEmits(['add', 'mouseover', 'mouseout', 'select'])
  const hasOnlyAlternativeResults = defineModel('alternativeResults', { type: Boolean })
  const props = defineProps({
    autoScroll: {
      type: Boolean,
      default: false,
    },

    avmNetwork: {
      type: String,
      required: true,
    },

    contractStartDate: {
      type: String,
      required: true,
    },

    hovered: {
      type: [String, null],
      default: null,
    },

    isAvmNetworkSpecific: {
      type: Boolean,
      default: true,
    },

    results: {
      type: Array,
      default: () => [],
    },

    selected: {
      type: [String, null],
      default: null,
    },

    showDetails: {
      type: Boolean,
      default: false,
    }
  })

  // COMPUTED
  const alternativeResults = computed(() => {
    if (props.isAvmNetworkSpecific) return []

    return props.results
  })

  const avmNetworkSpecificResults = computed(() => {
    if (props.isAvmNetworkSpecific) {
      return props.results
    }

    return props.results.filter(result => Object.hasOwn(result.discounts, props.avmNetwork))
  })

  const displayResults = computed(() => {
    if (props.isAvmNetworkSpecific) return avmNetworkSpecificResults.value

    return avmNetworkSpecificResults.value.length > 0 ? avmNetworkSpecificResults.value : alternativeResults.value
  })

  watchEffect(
    () => {
      hasOnlyAlternativeResults.value = avmNetworkSpecificResults.value.length === 0 && alternativeResults.value.length > 0
    }
  )

</script>

<style scoped>
.result-list {
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
}

.result-list__message {
  margin: var(--fixed-spacing-fix-04) var(--fixed-spacing-fix-02);
}

.result-list__alternative-results {
  margin: 0 0 var(--fixed-spacing-fix-02);
  padding: var(--dotcom-responsive-spacing-res-xxxs) 0 0;
  border-top: 1px solid var(--on-surface);
}
</style>
